/**
 * frontend/src/api/vouchers.js
 * Voucher system API calls for the main PriceMap frontend.
 *
 * `getOffers` ships with a module-level cache (5 min TTL) keyed by query
 * params. Every page that consumes the offer list — HomePage, DiscountsPage,
 * VoucherDetailPage — hits the cache on subsequent navigations within the
 * same tab. The cache survives React state resets but resets on full page
 * reload, which is exactly what we want (fresh data on refresh, instant on
 * back/forward).
 */

const BASE = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('pricemap_token');
  // If token is missing, don't add Authorization header — backend
  // will return 401 and frontend can redirect to login. This is better
  // than sending "Bearer null" which causes confusing error messages.
  if (!token || typeof token !== 'string' || token.trim().length === 0) {
    return { 'Content-Type': 'application/json' };
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token.trim()}`,
  };
};

async function handle(res) {
  // If the token is stale or the account state changed server-side
  // (deactivated / banned), wipe local credentials so the next page
  // render redirects to /auth instead of looping 401/403 forever.
  // This fixes a PWA-specific hang where a cached token survives a
  // server-side session invalidation.
  if (res.status === 401 || res.status === 403) {
    localStorage.removeItem('pricemap_token');
    localStorage.removeItem('pricemap_user');
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'შეცდომა');
  return data;
}

// ─── getOffers cache ───
const CACHE_TTL = 5 * 60 * 1000;
const _offersCache  = new Map();   // key → { data, ts }
const _inflight     = new Map();   // key → Promise (request dedup)

// [2026-06-13] Persistent layer (stale-while-revalidate support):
// the in-memory map dies on every page refresh, so PWA reloads always
// refetched. localStorage keeps the last seen list per query for
// LS_TTL — the voucher row paints instantly after a refresh and the
// network is only hit when the entry is older than the TTL (or after
// a manual refresh via clearOffersCache).
const LS_PREFIX = 'pm_offers:';
// [SWR] Max staleness we'll paint before a blocking fetch. With
// stale-while-revalidate the list is refreshed in the background on every
// stale hit, so this only bounds how old the *first* paint can be. Dropped
// from 6h → 10min so a newly added offer surfaces quickly instead of being
// hidden behind a long-lived cache.
const LS_TTL = 10 * 60 * 1000;   // 10 min

function lsRead(key) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || !Array.isArray(v.data) || Date.now() - v.ts > LS_TTL) return null;
    return v;
  } catch { return null; }
}

function lsWrite(key, data) {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* quota/private mode — persistent layer is best-effort */ }
}

function lsClearAll() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

function cacheKey({ lat, lng, multi_use, category }) {
  // Round coords to ~110m precision so tiny movement doesn't bust the cache.
  // [2026-06-12] category added to the key — without it, switching the club
  // filter returned the cached UNFILTERED list (same key) and the request
  // with ?category= never fired, so the filter appeared dead.
  const la = lat != null ? Number(lat).toFixed(3) : '';
  const ln = lng != null ? Number(lng).toFixed(3) : '';
  return `${la}|${ln}|${multi_use ? '1' : '0'}|${category || '-'}`;
}

// Actual network fetch for the offers list. Dedupes concurrent calls per key
// via _inflight, and on success refreshes both cache layers. Used by both the
// cold path (awaited) and the background revalidation (fire-and-forget).
function _fetchOffers(key, opts) {
  const inflight = _inflight.get(key);
  if (inflight) return inflight;

  const params = new URLSearchParams();
  if (opts.lat != null) params.set('lat', String(opts.lat));
  if (opts.lng != null) params.set('lng', String(opts.lng));
  if (opts.multi_use)   params.set('multi_use', 'true');
  if (opts.category)    params.set('category', opts.category);
  const qs = params.toString();
  const url = `${BASE}/api/voucher-offers${qs ? `?${qs}` : ''}`;

  const promise = fetch(url)
    .then(handle)
    .then((data) => {
      _offersCache.set(key, { data, ts: Date.now() });
      lsWrite(key, data);
      _inflight.delete(key);
      return data;
    })
    .catch((err) => {
      _inflight.delete(key);
      throw err;
    });

  _inflight.set(key, promise);
  return promise;
}

// [SWR] Background revalidation: refresh the cache silently. Never throws to
// the caller and never clears the existing cache on failure — a failed
// background refresh just leaves the last good data in place, so the UI is
// never broken by a flaky network. The next stale hit will try again.
function _revalidate(key, opts) {
  if (_inflight.has(key)) return;            // one already running
  _fetchOffers(key, opts).catch(() => {});   // swallow — keep stale data
}

export const voucherApi = {
  /** Public — active offers. Backend filters/sorts:
   *   - default: all active offers, by distance when lat/lng given
   *   - multi_use=true: only is_multi_use offers, by popularity
   * Stale-while-revalidate: a cached list paints instantly and is refreshed
   * in the background, so newly added offers appear on the next open/nav
   * (not after a long TTL). In-flight requests are deduplicated per query.
   */
  getOffers: (opts = {}) => {
    const key = cacheKey(opts);
    const now = Date.now();

    // Pick the best cached entry: in-memory first, else the persisted copy
    // (lsRead already discards anything older than LS_TTL).
    let entry = _offersCache.get(key);
    if (!entry) {
      const persisted = lsRead(key);
      if (persisted) {
        entry = { data: persisted.data, ts: persisted.ts };
        _offersCache.set(key, entry);   // seed memory from localStorage
      }
    }

    if (entry) {
      const age = now - entry.ts;
      // Fresh → serve instantly, no network.
      if (age < CACHE_TTL) return Promise.resolve(entry.data);
      // Stale but within LS_TTL → stale-while-revalidate: paint the cached
      // list immediately and refresh it in the background (silent). A newly
      // added offer lands in the cache here and shows on the next open/nav.
      if (age < LS_TTL) {
        _revalidate(key, opts);
        return Promise.resolve(entry.data);
      }
      // Older than LS_TTL → fall through to a blocking fetch.
    }

    // Cold (or too stale) → fetch and await.
    return _fetchOffers(key, opts);
  },

  /** Manual cache buster — call after a voucher action that should refresh
   * the list (e.g. admin updates, partner offer changes). Not needed for
   * normal user flows. */
  clearOffersCache: () => {
    _offersCache.clear();
    _inflight.clear();
    lsClearAll();
  },

  /** Generate a voucher — requires user auth */
  generate: (offer_id) =>
    fetch(`${BASE}/api/voucher/generate`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ offer_id }),
    }).then(handle),

  /** [Phase 1B] Confirm the one-time paid (1₾) voucher → reveals the code.
   *  Only called when generate() returned { requires_payment: true } with a
   *  voucher_id. Payment is a stub for now (no real bank call); the backend
   *  marks it paid and returns the full voucher with its code. */
  payVoucher: (voucher_id, payment_method = 'card') =>
    fetch(`${BASE}/api/voucher/pay`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ voucher_id, payment_method }),
    }).then(handle),
};
